import {
  AlertTriangle,
  CheckCircle2,
  ExternalLink,
  Eye,
  Flame,
  RefreshCw,
  ShoppingBag,
  Sparkles,
  XCircle,
} from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { ApiError, apiGet, apiPost } from '../api/client.js';
import { Button } from '../components/Button.js';
import { Card } from '../components/Card.js';
import { PageHeader } from '../components/PageHeader.js';
import { SafeHtml } from '../components/SafeHtml.js';
import { Skeleton } from '../components/Skeleton.js';

interface TrendItem {
  id: string;
  title: string;
  price: { value: number; currency: string } | null;
  imageUrl: string | null;
  itemUrl: string;
  categoryName: string;
  watchCount: number | null;
  seller: string | null;
}

interface Snapshot {
  source: 'ebay' | 'aliexpress';
  fetchedAt: string | null;
  items: TrendItem[];
  status: 'ok' | 'not_configured' | 'error';
  message?: string;
}

const SOURCE_META: Record<Snapshot['source'], { name: string; color: string; letter: string }> = {
  ebay: { name: 'eBay', color: '#0064d2', letter: 'e' },
  aliexpress: { name: 'AliExpress', color: '#e62e04', letter: 'A' },
};

function Logo({ source }: { source: Snapshot['source'] }): JSX.Element {
  const m = SOURCE_META[source];
  return (
    <div
      style={{
        width: 28,
        height: 28,
        borderRadius: 'var(--radius-sm)',
        background: m.color,
        color: '#fff',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontWeight: 700,
        fontSize: 14,
        fontStyle: source === 'ebay' ? 'italic' : 'normal',
        flexShrink: 0,
      }}
    >
      {m.letter}
    </div>
  );
}

function fmtPrice(p: TrendItem['price']): string {
  if (!p) return '—';
  return new Intl.NumberFormat('en-CA', {
    style: 'currency',
    currency: p.currency,
    maximumFractionDigits: 0,
  }).format(p.value);
}

function ProductCard({ item }: { item: TrendItem }): JSX.Element {
  const [drafting, setDrafting] = useState(false);
  const [draftResult, setDraftResult] = useState<{
    ok: boolean;
    reviewUrl?: string;
    msg?: string;
  } | null>(null);

  const draftToEbay = async (e: React.MouseEvent): Promise<void> => {
    e.preventDefault();
    e.stopPropagation();
    setDrafting(true);
    setDraftResult(null);
    try {
      const res = await apiPost<{ sku: string; reviewUrl: string }>(
        '/api/trends/draft-listing',
        {
          item: {
            title: item.title,
            imageUrl: item.imageUrl,
            priceCad: item.price?.value ?? 25,
            category: item.categoryName,
          },
        },
      );
      setDraftResult({ ok: true, reviewUrl: res.reviewUrl });
    } catch (err) {
      setDraftResult({
        ok: false,
        msg: err instanceof Error ? err.message : 'Draft failed.',
      });
    } finally {
      setDrafting(false);
    }
  };

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: 8,
        padding: 12,
        border: '1px solid var(--c-border)',
        borderRadius: 'var(--radius-lg)',
        background: 'var(--c-card)',
      }}
    >
      <a
        href={item.itemUrl}
        target="_blank"
        rel="noreferrer"
        style={{ textDecoration: 'none', color: 'inherit', display: 'contents' }}
      >
        <div
          style={{
            width: '100%',
            aspectRatio: '1 / 1',
            borderRadius: 'var(--radius-md)',
            background: 'var(--c-divider)',
            overflow: 'hidden',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          {item.imageUrl ? (
            // eslint-disable-next-line jsx-a11y/alt-text
            <img
              src={item.imageUrl}
              loading="lazy"
              style={{ width: '100%', height: '100%', objectFit: 'cover' }}
            />
          ) : (
            <span style={{ color: 'var(--c-placeholder)', fontSize: 11 }}>No image</span>
          )}
        </div>
        <div
          style={{
            fontSize: 'var(--text-base)',
            color: 'var(--c-text)',
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
            lineHeight: 1.35,
            minHeight: 34,
          }}
        >
          {item.title}
        </div>
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            fontSize: 'var(--text-sm)',
          }}
        >
          <span style={{ fontWeight: 600, color: 'var(--c-text)' }}>{fmtPrice(item.price)}</span>
          {item.watchCount !== null && (
            <span
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 3,
                color: 'var(--c-muted)',
              }}
            >
              <Eye size={11} />
              {item.watchCount}
            </span>
          )}
        </div>
      </a>
      <button
        type="button"
        onClick={draftToEbay}
        disabled={drafting}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 6,
          height: 30,
          fontSize: 'var(--text-base)',
          fontWeight: 500,
          background: 'var(--c-primary-soft)',
          color: 'var(--c-primary)',
          border: 'none',
          borderRadius: 'var(--radius-md)',
          cursor: drafting ? 'wait' : 'pointer',
          marginTop: 4,
        }}
      >
        <ShoppingBag size={13} strokeWidth={2.2} />
        {drafting ? 'Drafting…' : 'List on eBay'}
      </button>
      {draftResult?.ok && draftResult.reviewUrl && (
        <a
          href={draftResult.reviewUrl}
          target="_blank"
          rel="noreferrer"
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 6,
            fontSize: 'var(--text-xs)',
            color: 'var(--c-success)',
            background: 'var(--c-success-bg)',
            padding: '6px 10px',
            borderRadius: 'var(--radius-md)',
            textDecoration: 'none',
            fontWeight: 600,
          }}
        >
          <CheckCircle2 size={12} />
          Draft saved — finish in Seller Hub
        </a>
      )}
      {draftResult && !draftResult.ok && (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 6,
            fontSize: 'var(--text-xs)',
            color: 'var(--c-danger)',
            background: 'var(--c-danger-bg)',
            padding: '6px 10px',
            borderRadius: 'var(--radius-md)',
          }}
        >
          <XCircle size={12} />
          {draftResult.msg}
        </div>
      )}
    </div>
  );
}

function SnapshotCard({
  snap,
  refreshing,
  onRefresh,
}: {
  snap: Snapshot;
  refreshing: boolean;
  onRefresh: (() => void) | null;
}): JSX.Element {
  const m = SOURCE_META[snap.source];
  // Group items by category for a cleaner read.
  const grouped = useMemo(() => {
    const out: Record<string, TrendItem[]> = {};
    for (const item of snap.items) {
      (out[item.categoryName] ||= []).push(item);
    }
    return out;
  }, [snap.items]);

  return (
    <Card
      title={`${m.name} — best sellers`}
      description={
        snap.fetchedAt
          ? `Updated ${new Date(snap.fetchedAt).toLocaleString()}`
          : snap.status === 'not_configured'
            ? 'Not connected yet.'
            : 'No data yet.'
      }
      action={
        onRefresh ? (
          <Button size="sm" variant="secondary" icon={RefreshCw} loading={refreshing} onClick={onRefresh}>
            Refresh
          </Button>
        ) : null
      }
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
        <Logo source={snap.source} />
        {snap.status === 'not_configured' && (
          <span
            style={{
              fontSize: 'var(--text-xs)',
              color: 'var(--c-muted)',
              background: 'var(--c-divider)',
              padding: '3px 8px',
              borderRadius: 99,
              fontWeight: 500,
            }}
          >
            Not configured
          </span>
        )}
      </div>

      {snap.status === 'not_configured' && (
        <div
          style={{
            display: 'flex',
            gap: 10,
            padding: 12,
            borderRadius: 'var(--radius-md)',
            background: 'var(--c-warn-bg)',
            color: 'var(--c-warn)',
            fontSize: 'var(--text-md)',
          }}
        >
          <AlertTriangle size={18} style={{ flexShrink: 0 }} />
          <span>{snap.message ?? 'Not configured.'}</span>
        </div>
      )}

      {snap.status === 'error' && (
        <div
          style={{
            display: 'flex',
            gap: 10,
            padding: 12,
            borderRadius: 'var(--radius-md)',
            background: 'var(--c-danger-bg)',
            color: 'var(--c-danger)',
            fontSize: 'var(--text-md)',
          }}
        >
          <AlertTriangle size={18} style={{ flexShrink: 0 }} />
          <span>{snap.message}</span>
        </div>
      )}

      {snap.status === 'ok' && snap.items.length === 0 && (
        <p style={{ color: 'var(--c-muted)', fontSize: 'var(--text-md)' }}>
          No data yet. Click Refresh to pull the latest.
        </p>
      )}

      {snap.items.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
          {Object.entries(grouped).map(([cat, items]) => (
            <div key={cat}>
              <h3
                style={{
                  fontSize: 'var(--text-sm)',
                  fontWeight: 600,
                  color: 'var(--c-text-secondary)',
                  textTransform: 'uppercase',
                  letterSpacing: '0.04em',
                  marginBottom: 8,
                }}
              >
                {cat}
              </h3>
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))',
                  gap: 10,
                }}
              >
                {items.map((it) => (
                  <ProductCard key={it.id} item={it} />
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </Card>
  );
}

export function TrendsPage(): JSX.Element {
  const [snapshots, setSnapshots] = useState<Snapshot[] | null>(null);
  const [refreshing, setRefreshing] = useState<Snapshot['source'] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [digestHtml, setDigestHtml] = useState<string | null>(null);
  const [digestLoading, setDigestLoading] = useState(false);
  const [digestError, setDigestError] = useState<string | null>(null);

  const load = (): void => {
    apiGet<{ snapshots: Snapshot[] }>('/api/trends')
      .then((r) => setSnapshots(r.snapshots))
      .catch((e: unknown) =>
        setError(e instanceof ApiError ? e.message : 'Network error loading trends.'),
      );
  };

  useEffect(() => {
    load();
  }, []);

  const generateDigest = async (): Promise<void> => {
    setDigestLoading(true);
    setDigestError(null);
    setDigestHtml(null);
    try {
      const res = await apiPost<{ html: string }>('/api/trends/digest', {});
      setDigestHtml(res.html);
    } catch (e) {
      const msg = e instanceof Error ? e.message : 'Digest generation failed.';
      setDigestError(
        msg.includes('No AI provider configured')
          ? 'AI is not configured yet. Open Admin and pick a provider and paste your API key.'
          : msg.includes('no_data')
            ? 'Refresh at least one source first.'
            : msg,
      );
    } finally {
      setDigestLoading(false);
    }
  };

  const hasAnyData = useMemo(
    () => Boolean(snapshots?.some((s) => s.items.length > 0)),
    [snapshots],
  );

  const refresh = async (source: Snapshot['source']): Promise<void> => {
    setRefreshing(source);
    setError(null);
    try {
      await apiPost('/api/trends/refresh', { source });
      load();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Refresh failed.');
    } finally {
      setRefreshing(null);
    }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
      <PageHeader
        title="Trends"
        accent="what's hot"
        icon={<Flame size={20} color="var(--primary)" strokeWidth={2.2} />}
        sub="Top-selling products across eBay and AliExpress, refreshed on demand."
      />

      {error && (
        <div
          style={{
            padding: 12,
            borderRadius: 'var(--radius-md)',
            background: 'var(--c-danger-bg)',
            color: 'var(--c-danger)',
            fontSize: 'var(--text-md)',
          }}
        >
          {error}
        </div>
      )}

      {snapshots === null ? (
        <Card>
          <Skeleton height={200} />
        </Card>
      ) : (
        snapshots.map((s) => (
          <SnapshotCard
            key={s.source}
            snap={s}
            refreshing={refreshing === s.source}
            onRefresh={() => refresh(s.source)}
          />
        ))
      )}

      <Card
        title="AI category digest"
        description="A targeted sourcing brief: top categories, cross-platform matches, and what to avoid — generated by Claude from the snapshots above."
        action={
          <Button
            size="sm"
            icon={Sparkles}
            loading={digestLoading}
            disabled={!hasAnyData}
            onClick={generateDigest}
          >
            {digestHtml ? 'Regenerate' : 'Generate digest'}
          </Button>
        }
      >
        {!hasAnyData && (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 12,
              padding: 14,
              borderRadius: 'var(--radius-md)',
              background: 'var(--c-divider)',
              color: 'var(--c-muted)',
              fontSize: 'var(--text-md)',
            }}
          >
            <Sparkles size={18} />
            <span>Refresh at least one source above to enable the digest.</span>
          </div>
        )}
        {digestLoading && <Skeleton height={140} />}
        {digestError && (
          <div
            style={{
              padding: 12,
              borderRadius: 'var(--radius-md)',
              background: 'var(--c-danger-bg)',
              color: 'var(--c-danger)',
              fontSize: 'var(--text-md)',
            }}
          >
            {digestError}
          </div>
        )}
        {digestHtml && (
          <div style={{ fontSize: 'var(--text-md)', lineHeight: 1.6 }}>
            <SafeHtml html={digestHtml} />
          </div>
        )}
      </Card>

      <p
        style={{
          fontSize: 'var(--text-sm)',
          color: 'var(--c-muted)',
          marginTop: -8,
          display: 'inline-flex',
          alignItems: 'center',
          gap: 4,
        }}
      >
        Powered by eBay Buy Browse API
        <ExternalLink size={11} />
      </p>
    </div>
  );
}
